<script setup>
import { Link, router, usePage, useForm } from '@inertiajs/vue3'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiModal from '@/Components/UI/UiModal.vue'
import Icon from '@/Components/UI/Icon.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import StatusBadge from '@/Components/UI/StatusBadge.vue'
import UiStatusPill from '@/Components/UI/UiStatusPill.vue'
import ActionCluster from '@/Components/UI/ActionCluster.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import ReportDamageModal from '@/Components/Damage/ReportDamageModal.vue'
import MarkRepairedModal from '@/Components/Damage/MarkRepairedModal.vue'
import DamageRow from '@/Components/Damage/DamageRow.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import MarkSubRentedModal from '@/Components/Suppliers/MarkSubRentedModal.vue'
import RecordSupplierPaymentModal from '@/Components/Suppliers/RecordSupplierPaymentModal.vue'

defineOptions({ layout: AppLayout })

const props = defineProps({
  vehicle:                { type: Object, required: true },
  utilization_data:       { type: Array,  default: () => [] },
  open_maintenance:       { type: Object, default: null },
  maintenance_history:    { type: Array,  default: () => [] },
  can_manage_maintenance: { type: Boolean, default: false },
  damages:               { type: Array,   default: () => [] },
  can_manage_damage:     { type: Boolean, default: false },
  current_sub_rental:    { type: Object,  default: null },
  sub_rental_history:    { type: Array,   default: () => [] },
  available_sources:     { type: Array,   default: () => [] },
  supplier_conversation_id: { type: Number, default: null },
  released_from_supplier: { type: Boolean, default: false },
  renew_prefill:         { type: Object,  default: null },
  supplier_blocks:       { type: Array,   default: () => [] },
})

const supplierContactHref = computed(() => {
  if (!props.current_sub_rental) return null
  return props.supplier_conversation_id
    ? `/inbox/conversations/${props.supplier_conversation_id}`
    : `/vehicle_sources/${props.current_sub_rental.source_id}`
})

const { formatMoney, formatDate } = useCurrency()
const { t } = useI18n()

const showDeleteConfirm = ref(false)

const goBack = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) window.history.back()
  else router.visit('/vehicles')
}

// ── Maintenance open/close modals ─────────────────────────────
const showOpenModal  = ref(false)
const showCloseModal = ref(false)

const openForm = useForm({
  description:      '',
  cost_cents:       '',
  planned_end_date: '',
})
const closeForm = useForm({
  cost_cents: '',
  notes:      '',
})

const openMaintenance = () => {
  openForm.transform((d) => ({
    maintenance_log: {
      description:      d.description,
      cost_cents:       d.cost_cents === '' ? 0 : Math.round(Number(d.cost_cents) * 100),
      planned_end_date: d.planned_end_date || null,
    },
  })).post(`/vehicles/${props.vehicle.id}/maintenance_logs`, {
    onSuccess: () => { showOpenModal.value = false; openForm.reset() },
  })
}

const startCloseFlow = () => {
  closeForm.cost_cents = (props.open_maintenance?.cost_cents ?? 0) / 100
  closeForm.notes = ''
  showCloseModal.value = true
}
const closeMaintenance = () => {
  closeForm.transform((d) => ({
    maintenance_log: {
      cost_cents: d.cost_cents === '' ? 0 : Math.round(Number(d.cost_cents) * 100),
      notes:      d.notes,
    },
  })).patch(`/vehicles/${props.vehicle.id}/maintenance_logs/${props.open_maintenance.id}/close`, {
    onSuccess: () => { showCloseModal.value = false; closeForm.reset() },
  })
}

const maxCount = computed(() =>
  Math.max(...(props.utilization_data?.map(b => b.count) || [0]), 1)
)

const nextServiceOverdue = computed(() => {
  const target = props.vehicle.next_service_km
  const current = props.vehicle.odometer_km
  return target != null && current != null && current >= target
})

const headerActions = computed(() => {
  const a = []
  // When the vehicle is released by the supplier, the only meaningful
  // action is archiving — everything else (rentals, maintenance, edit)
  // is gated until the operator renews the sub-rental.
  if (props.released_from_supplier) {
    a.push({
      key: 'delete',
      label: t('vehicles.show.delete.confirm'),
      icon: 'x',
      variant: 'danger',
      onClick: () => { showDeleteConfirm.value = true },
    })
    return a
  }
  // Maintenance toggle — close (active) is primary, open is primary when no active
  if (props.can_manage_maintenance && props.open_maintenance) {
    a.push({
      key: 'closeMaint',
      label: t('vehicles.maintenance.close'),
      icon: 'check',
      variant: 'primary',
      onClick: startCloseFlow,
      primary: true,
    })
  } else if (props.can_manage_maintenance && props.vehicle.status === 'available') {
    a.push({
      key: 'openMaint',
      label: t('vehicles.maintenance.open'),
      icon: 'bolt',
      variant: 'primary',
      onClick: () => { showOpenModal.value = true },
      primary: true,
    })
  }
  a.push({
    key: 'gps',
    label: t('rental.show.gps'),
    icon: 'pin',
    onClick: () => router.visit(`/vehicles/${props.vehicle.id}/gps`),
  })
  a.push({
    key: 'edit',
    label: t('common.edit'),
    icon: 'sign',
    onClick: () => router.visit(`/vehicles/${props.vehicle.id}/edit`),
  })
  a.push({
    key: 'delete',
    label: t('vehicles.show.delete.confirm'),
    icon: 'x',
    variant: 'danger',
    onClick: () => { showDeleteConfirm.value = true },
  })
  return a
})

function confirmDelete() {
  showDeleteConfirm.value = false
  router.delete(`/vehicles/${props.vehicle.id}`)
}

// ── Damage history ────────────────────────────────────────────
const page = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')
const canEditVehicle = computed(() => ['admin', 'staff'].includes(page.props.auth?.user?.role))

// Inline notes editing — admin/staff can click "Edit", type, and save
// without leaving the show page. PATCH only sends the notes attribute
// so we don't accidentally clear other vehicle fields.
const notesEditing = ref(false)
const notesDraft   = ref(props.vehicle.notes || '')
const notesSaving  = ref(false)

watch(() => props.vehicle.notes, (n) => {
  if (!notesEditing.value) notesDraft.value = n || ''
})

function startEditingNotes() {
  notesDraft.value = props.vehicle.notes || ''
  notesEditing.value = true
}

function saveNotes() {
  notesSaving.value = true
  router.patch(`/vehicles/${props.vehicle.id}`,
    { vehicle: { notes: notesDraft.value } },
    {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => { notesSaving.value = false },
      onSuccess: () => { notesEditing.value = false }
    }
  )
}

function cancelEditingNotes() {
  notesDraft.value = props.vehicle.notes || ''
  notesEditing.value = false
}

const reportOpen   = ref(false)
const repairOpen   = ref(false)
const editingDamage = ref(null)

const openReport = () => { editingDamage.value = null; reportOpen.value = true }
const openEdit   = (d) => { editingDamage.value = d; reportOpen.value = true }
const openRepair = (d) => { editingDamage.value = d; repairOpen.value = true }

const onDelete = (d) => {
  if (!confirm(t('damage.confirmDelete'))) return
  router.delete(`/damages/${d.id}`, { preserveScroll: true })
}

// ── Source / sub-rental ───────────────────────────────────────
const markSubRentedOpen = ref(false)
const paymentModalOpen  = ref(false)

function endSubRental() {
  if (!props.current_sub_rental) return
  if (!confirm(t('suppliers.vehicleCard.endConfirm'))) return
  router.patch(`/sub_rentals/${props.current_sub_rental.id}/end`, {}, { preserveScroll: true })
}

// ── Photo gallery lightbox ───────────────────────────────────
const lightboxIndex = ref(null)
const lightboxOpen = computed(() => lightboxIndex.value !== null)
const photoCount = computed(() => props.vehicle.photo_urls?.length || 0)

const openLightbox = (i) => { lightboxIndex.value = i }
const closeLightbox = () => { lightboxIndex.value = null }
const lightboxNext = () => { if (photoCount.value) lightboxIndex.value = (lightboxIndex.value + 1) % photoCount.value }
const lightboxPrev = () => { if (photoCount.value) lightboxIndex.value = (lightboxIndex.value - 1 + photoCount.value) % photoCount.value }

const onLightboxKey = (e) => {
  if (!lightboxOpen.value) return
  if (e.key === 'Escape')     closeLightbox()
  if (e.key === 'ArrowRight') lightboxNext()
  if (e.key === 'ArrowLeft')  lightboxPrev()
}

onMounted(() => window.addEventListener('keydown', onLightboxKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onLightboxKey))

// ── Documents ────────────────────────────────────────────────
const docModalOpen = ref(false)
const docForm = ref({ kind: 'insurance', label: '', file: null })

const docKindOptions = computed(() => [
  { value: 'insurance',    label: t('vehicles.show.documents.kinds.insurance') },
  { value: 'registration', label: t('vehicles.show.documents.kinds.registration') },
  { value: 'other',        label: t('vehicles.show.documents.kinds.other') },
])

const submitDocument = () => {
  if (!docForm.value.file) return
  const fd = new FormData()
  fd.append('vehicle_document[kind]',  docForm.value.kind)
  fd.append('vehicle_document[label]', docForm.value.label || '')
  fd.append('vehicle_document[file]',  docForm.value.file)
  router.post(`/vehicles/${props.vehicle.id}/documents`, fd, {
    preserveScroll: true,
    onSuccess: () => {
      docModalOpen.value = false
      docForm.value = { kind: 'insurance', label: '', file: null }
    },
  })
}

const deleteDocument = (d) => {
  if (!confirm(t('vehicles.show.documents.confirmDelete'))) return
  router.delete(`/vehicle_documents/${d.id}`, { preserveScroll: true })
}
</script>

<template>
  <PageShell :title="t('nav.fleet')">
    <div class="flex items-center justify-between mb-4 gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
          :aria-label="t('common.back')"
          @click="goBack"
        >
          <Icon
            name="chev-l"
            :size="18"
          />
        </button>
        <h2 class="text-xl font-semibold text-[color:var(--ink)] truncate flex items-center gap-2 flex-wrap">
          <span>
            {{ vehicle.make }} {{ vehicle.model }}
            <span class="text-[color:var(--muted)] font-normal">{{ vehicle.year }}</span>
          </span>
          <span
            v-if="vehicle.currently_sub_rented"
            class="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full"
            :style="{ background: 'color-mix(in oklab, var(--warn) 18%, transparent)', color: 'var(--warn)' }"
          >
            {{ t('vehicles.subRentedBadge') }}
          </span>
        </h2>
      </div>
      <ActionCluster :actions="headerActions" />
    </div>

    <!-- Current maintenance banner -->
    <div
      v-if="open_maintenance"
      class="rounded-xl p-5 mb-6"
      :style="{ background: 'var(--warnSoft, color-mix(in oklch, var(--warn) 12%, var(--surface)))', border: '1px solid var(--warn)' }"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <Icon
              name="alert-triangle"
              :size="16"
              :style="{ color: 'var(--warn)' }"
            />
            <h3
              class="text-sm font-semibold"
              :style="{ color: 'var(--warn)' }"
            >
              {{ t('vehicles.maintenance.currentTitle') }}
            </h3>
          </div>
          <p class="text-sm text-[color:var(--ink)] whitespace-pre-line">
            {{ open_maintenance.description }}
          </p>
          <dl class="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.started') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatDate(open_maintenance.started_at) }}
              </dd>
            </div>
            <div v-if="open_maintenance.planned_end_date">
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.plannedEnd') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatDate(open_maintenance.planned_end_date) }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.cost') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatMoney(open_maintenance.cost_cents) }}
              </dd>
            </div>
            <div v-if="open_maintenance.started_by">
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.openedBy') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ open_maintenance.started_by }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Details -->
      <div class="lg:col-span-2 space-y-6">
        <div
          class="rounded-xl p-6"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <div class="flex items-center gap-3 mb-4">
            <StatusBadge
              type="vehicle"
              :status="vehicle.status"
            />
            <UiStatusPill
              v-if="vehicle.vehicle_class"
              tone="muted"
              class="capitalize"
              :label="`${t(`vehicles.classes.${vehicle.vehicle_class}`)} · ${vehicle.category}`"
            />
            <span
              v-else
              class="text-sm text-[color:var(--muted)] capitalize"
            >{{ vehicle.category }}</span>
          </div>

          <dl class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.registration') }}
              </dt>
              <dd class="font-mono fw-value text-[color:var(--ink)] mt-0.5">
                {{ vehicle.registration }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.colour') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ vehicle.colour || '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.defaultRate') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ vehicle.daily_rate }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.deposit') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ formatMoney(vehicle.deposit_amount_cents) }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.odometer') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ vehicle.odometer_km != null ? t('vehicles.show.fields.odometerValue', { km: vehicle.odometer_km }) : '—' }}
              </dd>
            </div>
            <div v-if="vehicle.engine_cc != null || vehicle.horsepower != null">
              <dt class="text-[color:var(--muted)]">Engine</dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                <span v-if="vehicle.engine_cc">{{ vehicle.engine_cc }}cc</span>
                <span v-if="vehicle.engine_cc && vehicle.horsepower" class="text-[color:var(--muted)] mx-1">·</span>
                <span v-if="vehicle.horsepower">{{ vehicle.horsepower }}hp</span>
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.lastService') }}
              </dt>
              <dd class="fw-value text-[color:var(--ink)] mt-0.5">
                {{ vehicle.last_service_km != null ? t('vehicles.show.fields.odometerValue', { km: vehicle.last_service_km }) : '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-[color:var(--muted)]">
                {{ t('vehicles.show.fields.nextService') }}
              </dt>
              <dd
                class="fw-value mt-0.5"
                :class="nextServiceOverdue ? 'text-[color:var(--danger)]' : 'text-[color:var(--ink)]'"
              >
                {{ vehicle.next_service_km != null ? t('vehicles.show.fields.odometerValue', { km: vehicle.next_service_km }) : '—' }}
              </dd>
            </div>
            <template v-if="vehicle.vehicle_class === 'four_wheeler'">
              <div>
                <dt class="text-[color:var(--muted)]">
                  {{ t('vehicles.fields.transmission') }}
                </dt>
                <dd class="fw-value mt-0.5 capitalize">
                  {{ vehicle.transmission?.replace('_', ' ') ?? '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-[color:var(--muted)]">
                  {{ t('vehicles.fields.seats') }}
                </dt>
                <dd class="fw-value mt-0.5">
                  {{ vehicle.seats ?? '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-[color:var(--muted)]">
                  {{ t('vehicles.fields.fuel_type') }}
                </dt>
                <dd class="fw-value mt-0.5 capitalize">
                  {{ vehicle.fuel_type ?? '—' }}
                </dd>
              </div>
            </template>
          </dl>
        </div>

        <!-- Photos -->
        <div
          v-if="vehicle.photo_urls?.length"
          class="rounded-xl p-6"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <h2 class="text-lg font-semibold text-[color:var(--ink)] mb-4">
            {{ t('vehicles.show.photos') }}
          </h2>
          <div class="grid grid-cols-3 gap-3">
            <button
              v-for="(url, i) in vehicle.photo_urls"
              :key="i"
              type="button"
              class="block rounded-lg overflow-hidden transition focus:outline-2 focus:outline-[color:var(--accent)]"
              :aria-label="t('vehicles.show.photoAlt', { n: i + 1 })"
              @click="openLightbox(i)"
            >
              <img
                :src="url"
                :alt="t('vehicles.show.photoAlt', { n: i + 1 })"
                class="object-cover w-full h-32 hover:opacity-90 transition"
                :style="{ border: '1px solid var(--line)' }"
              >
            </button>
          </div>
        </div>

        <!-- Lightbox overlay — Teleport to body so a transform on any
             ancestor (sidebar, page shell, etc.) can't create a containing
             block that traps `position: fixed` inside the document flow. -->
        <Teleport to="body">
          <div
            v-if="lightboxOpen"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 select-none"
            role="dialog"
            aria-modal="true"
            @click.self="closeLightbox"
          >
            <button
              type="button"
              class="absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white"
              :aria-label="t('common.close')"
              @click="closeLightbox"
            >
              <Icon name="x" :size="20" />
            </button>
            <button
              v-if="photoCount > 1"
              type="button"
              class="absolute left-3 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white"
              aria-label="Previous"
              @click.stop="lightboxPrev"
            >
              <Icon name="chev-l" :size="22" />
            </button>
            <img
              :src="vehicle.photo_urls[lightboxIndex]"
              :alt="t('vehicles.show.photoAlt', { n: lightboxIndex + 1 })"
              class="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-xl"
            >
            <button
              v-if="photoCount > 1"
              type="button"
              class="absolute right-3 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white"
              aria-label="Next"
              @click.stop="lightboxNext"
            >
              <Icon name="chev-r" :size="22" />
            </button>
            <div
              v-if="photoCount > 1"
              class="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-mono tabular-nums"
            >
              {{ lightboxIndex + 1 }} / {{ photoCount }}
            </div>
          </div>
        </Teleport>

        <!-- Documents -->
        <div
          class="rounded-xl p-6"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-[color:var(--ink)]">
              {{ t('vehicles.show.documents.title') }}
            </h2>
            <UiButton
              v-if="canEditVehicle"
              variant="secondary"
              size="sm"
              icon-left="plus"
              @click="docModalOpen = true"
            >
              {{ t('vehicles.show.documents.add') }}
            </UiButton>
          </div>

          <ul
            v-if="vehicle.documents?.length"
            role="list"
            class="divide-y divide-[color:var(--line)]"
          >
            <li
              v-for="d in vehicle.documents"
              :key="d.id"
              class="flex items-center gap-3 py-2.5"
            >
              <span
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
              >
                {{ t(`vehicles.show.documents.kinds.${d.kind}`) }}
              </span>
              <a
                :href="d.url"
                target="_blank"
                class="flex-1 truncate text-sm text-[color:var(--ink)] hover:underline"
              >
                {{ d.label || d.filename || t(`vehicles.show.documents.kinds.${d.kind}`) }}
              </a>
              <span class="text-xs text-[color:var(--muted)]">{{ formatDate(d.uploaded_at) }}</span>
              <button
                v-if="canEditVehicle"
                type="button"
                class="text-xs text-[color:var(--danger)] hover:underline"
                @click="deleteDocument(d)"
              >
                {{ t('common.delete') }}
              </button>
            </li>
          </ul>
          <p
            v-else
            class="text-sm text-[color:var(--muted)] py-2"
          >
            {{ t('vehicles.show.documents.empty') }}
          </p>
        </div>

        <UiModal
          v-if="docModalOpen"
          :open="docModalOpen"
          :title="t('vehicles.show.documents.add')"
          @close="docModalOpen = false"
        >
          <form
            id="vehicle-doc-form"
            class="space-y-4"
            enctype="multipart/form-data"
            @submit.prevent="submitDocument"
          >
            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
                {{ t('vehicles.show.documents.kind') }}
              </label>
              <UiSelect
                v-model="docForm.kind"
                :options="docKindOptions"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
                {{ t('vehicles.show.documents.label') }}
                <span v-if="docForm.kind === 'other'" class="text-[color:var(--danger)]">*</span>
              </label>
              <UiInput
                v-model="docForm.label"
                type="text"
                :placeholder="t('vehicles.show.documents.labelPlaceholder')"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
                {{ t('vehicles.show.documents.file') }}
              </label>
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
                @change="docForm.file = $event.target.files[0] || null"
              >
            </div>
          </form>
          <template #footer>
            <UiButton variant="secondary" @click="docModalOpen = false">
              {{ t('common.cancel') }}
            </UiButton>
            <UiButton
              variant="primary"
              type="submit"
              form="vehicle-doc-form"
              :disabled="!docForm.file || (docForm.kind === 'other' && !docForm.label)"
            >
              {{ t('common.save') }}
            </UiButton>
          </template>
        </UiModal>

        <!-- Utilization bar chart -->
        <div
          class="rounded-xl p-5"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <h2 class="text-sm font-semibold text-[color:var(--inkSoft)] mb-4">
            {{ t('vehicles.utilisation') }}
          </h2>
          <div
            v-if="utilization_data?.length"
            class="flex items-end gap-1.5 h-20"
            role="img"
            :aria-label="t('vehicles.utilisation')"
          >
            <div
              v-for="bar in utilization_data"
              :key="bar.month"
              class="flex-1 flex flex-col items-center gap-1"
            >
              <div
                class="w-full rounded-t transition-all"
                :style="{
                  background: bar.count > 0 ? 'var(--accent)' : 'var(--surfaceAlt)',
                  height: `${Math.max((bar.count / maxCount) * 60, bar.count > 0 ? 4 : 2)}px`
                }"
                :title="t('vehicles.show.barTooltip', { label: bar.label, count: bar.count })"
              />
              <span class="text-xs text-[color:var(--muted)] select-none">{{ bar.label }}</span>
            </div>
          </div>
          <p
            v-else
            class="text-sm text-[color:var(--muted)]"
          >
            {{ t('vehicles.noUtilisationData') }}
          </p>
        </div>
      </div>

      <!-- Notes (inline-editable, second column) -->
      <div class="space-y-6">
        <div
          class="rounded-xl p-6"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
        >
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-lg font-semibold text-[color:var(--ink)]">
              {{ t('vehicles.show.notes') }}
            </h2>
            <UiButton
              v-if="!notesEditing && canEditVehicle"
              variant="ghost"
              size="sm"
              @click="startEditingNotes"
            >
              {{ vehicle.notes ? t('common.edit') : t('common.add') }}
            </UiButton>
          </div>
          <div v-if="notesEditing" class="space-y-2">
            <UiTextarea
              v-model="notesDraft"
              :rows="6"
              :placeholder="t('vehicles.show.notesPlaceholder')"
              autofocus
            />
            <div class="flex gap-2">
              <UiButton
                variant="primary"
                size="sm"
                :loading="notesSaving"
                @click="saveNotes"
              >
                {{ t('common.save') }}
              </UiButton>
              <UiButton
                variant="ghost"
                size="sm"
                :disabled="notesSaving"
                @click="cancelEditingNotes"
              >
                {{ t('common.cancel') }}
              </UiButton>
            </div>
          </div>
          <p
            v-else
            class="text-sm text-[color:var(--inkSoft)] whitespace-pre-line"
          >
            {{ vehicle.notes || t('vehicles.show.noNotes') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Rental history -->
    <div
      class="rounded-xl overflow-hidden mt-6"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <div
        class="px-6 py-4 flex items-center justify-between"
        :style="{ borderBottom: '1px solid var(--line)' }"
      >
        <h2 class="text-lg font-semibold text-[color:var(--ink)]">
          {{ t('vehicles.show.rentalHistory') }}
        </h2>
        <Link
          href="/rentals"
          class="text-sm text-[color:var(--accent)] hover:underline"
        >
          {{ t('common.viewAll') }}
        </Link>
      </div>
      <div
        v-if="!vehicle.rentals?.length"
        class="px-6 py-10 text-center text-sm text-[color:var(--muted)]"
      >
        {{ t('vehicles.show.noRentals') }}
      </div>
      <div v-else>
        <!-- Desktop -->
        <div class="hidden md:block overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  #
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rentals.columns.customer') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rental.show.fields.start') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rental.show.fields.expectedReturn') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('rentals.columns.status') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                  {{ t('common.estimatedTotal') }}
                </th>
                <th class="relative px-4 py-3">
                  <span class="sr-only">{{ t('common.view') }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, i) in vehicle.rentals"
                :key="r.id"
                class="transition-colors hover:bg-[color:var(--surfaceAlt)]"
                :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
              >
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--muted)] font-mono">
                  #{{ r.id }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-[color:var(--ink)]">
                  {{ r.customer_name }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--inkSoft)]">
                  {{ formatDate(r.start_at) }}
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm"
                  :class="r.overdue ? 'text-[color:var(--danger)] font-medium' : 'text-[color:var(--inkSoft)]'"
                >
                  {{ formatDate(r.expected_end_at) }}
                  <span
                    v-if="r.overdue"
                    class="ml-1 text-xs px-1.5 py-0.5 rounded-full"
                    :style="{ background: 'var(--dangerSoft)', color: 'var(--danger)' }"
                  >
                    {{ t('rentals.status.overdue') }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <StatusBadge
                    type="rental"
                    :status="r.status"
                    :overdue="r.overdue"
                  />
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm text-[color:var(--ink)]">
                  {{ r.estimated_total }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-right text-sm">
                  <Link
                    :href="`/rentals/${r.id}`"
                    class="text-[color:var(--accent)] hover:underline"
                  >
                    {{ t('common.view') }}
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Mobile cards -->
        <div class="md:hidden divide-y divide-[color:var(--line)]">
          <Link
            v-for="r in vehicle.rentals"
            :key="r.id"
            :href="`/rentals/${r.id}`"
            class="block px-4 py-3 hover:bg-[color:var(--surfaceAlt)] transition-colors"
          >
            <div class="flex items-start justify-between gap-2 mb-1.5">
              <div class="min-w-0 flex-1">
                <div class="text-sm fw-value text-[color:var(--ink)] truncate">
                  {{ r.customer_name }}
                </div>
                <div class="text-xs text-[color:var(--muted)] font-mono mt-0.5">
                  #{{ r.id }}
                </div>
              </div>
              <StatusBadge
                type="rental"
                :status="r.status"
                :overdue="r.overdue"
              />
            </div>
            <div class="flex items-center justify-between text-xs gap-2">
              <span :class="r.overdue ? 'text-[color:var(--danger)] font-medium' : 'text-[color:var(--inkSoft)]'">
                {{ formatDate(r.start_at) }} → {{ formatDate(r.expected_end_at) }}
              </span>
              <span class="tabular-nums text-[color:var(--ink)] fw-value shrink-0">{{ r.estimated_total }}</span>
            </div>
          </Link>
        </div>
      </div>
    </div>

    <!-- Maintenance history -->
    <div
      v-if="maintenance_history?.length"
      class="rounded-xl overflow-hidden mt-6"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <div
        class="px-6 py-4"
        :style="{ borderBottom: '1px solid var(--line)' }"
      >
        <h2 class="text-lg font-semibold text-[color:var(--ink)]">
          {{ t('vehicles.maintenance.historyTitle') }}
        </h2>
      </div>
      <!-- Desktop -->
      <div class="hidden md:block overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.started') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.ended') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.description') }}
              </th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.maintenance.fields.cost') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(l, i) in maintenance_history"
              :key="l.id"
              :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
            >
              <td class="px-4 py-3 whitespace-nowrap text-[color:var(--inkSoft)]">
                {{ formatDate(l.started_at) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-[color:var(--inkSoft)]">
                {{ l.ended_at ? formatDate(l.ended_at) : '—' }}
              </td>
              <td class="px-4 py-3 text-[color:var(--ink)]">
                {{ l.description }}
                <span
                  v-if="l.notes"
                  class="block text-xs text-[color:var(--muted)] mt-0.5"
                >
                  {{ l.notes }}
                </span>
              </td>
              <td class="px-4 py-3 text-right fw-value tabular-nums text-[color:var(--ink)]">
                {{ formatMoney(l.cost_cents) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Mobile cards -->
      <div class="md:hidden divide-y divide-[color:var(--line)]">
        <div
          v-for="l in maintenance_history"
          :key="l.id"
          class="px-4 py-3"
        >
          <div class="flex items-start justify-between gap-2 mb-1">
            <div class="min-w-0 flex-1">
              <div class="text-sm fw-value text-[color:var(--ink)]">
                {{ l.description }}
              </div>
              <div
                v-if="l.notes"
                class="text-xs text-[color:var(--muted)] mt-0.5 whitespace-pre-line"
              >
                {{ l.notes }}
              </div>
            </div>
            <span class="text-sm fw-value tabular-nums text-[color:var(--ink)] shrink-0">{{ formatMoney(l.cost_cents) }}</span>
          </div>
          <div class="text-xs text-[color:var(--muted)] tabular-nums">
            {{ formatDate(l.started_at) }} → {{ l.ended_at ? formatDate(l.ended_at) : '—' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Damage history -->
    <div
      class="rounded-xl overflow-hidden mt-6"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <div
        class="px-6 py-4 flex items-center justify-between"
        :style="{ borderBottom: '1px solid var(--line)' }"
      >
        <div class="flex items-baseline gap-2">
          <h2 class="text-lg font-semibold text-[color:var(--ink)]">
            {{ t('damage.history.title') }}
          </h2>
          <span class="text-xs text-[color:var(--muted)] tabular-nums">{{ t('damage.history.count', { n: damages.length }) }}</span>
        </div>
        <UiButton
          v-if="can_manage_damage"
          variant="primary"
          size="sm"
          icon-left="plus"
          @click="openReport"
        >
          {{ t('damage.report') }}
        </UiButton>
      </div>
      <div class="px-6 py-4">
        <EmptyState
          v-if="damages.length === 0"
          type="success"
          :title="t('damage.empty')"
        />
        <div
          v-else
          class="divide-y divide-[color:var(--line)]"
        >
          <DamageRow
            v-for="d in damages"
            :key="d.id"
            :damage="d"
            :can-manage="can_manage_damage"
            :can-delete="isAdmin"
            @mark-repaired="openRepair"
            @edit="openEdit"
            @delete="onDelete"
          />
        </div>
      </div>
    </div>

    <!-- Supplier blocks — read-only; only shown when at least one block exists -->
    <div
      v-if="supplier_blocks?.length"
      class="rounded-xl overflow-hidden mt-6"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <div
        class="px-6 py-4"
        :style="{ borderBottom: '1px solid var(--line)' }"
      >
        <h2 class="text-lg font-semibold text-[color:var(--ink)]">
          {{ t('vehicles.supplierBlocks.title') }}
        </h2>
      </div>
      <!-- Desktop -->
      <div class="hidden md:block overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.supplierBlocks.cols.dates') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.supplierBlocks.cols.status') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.supplierBlocks.cols.reason') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                {{ t('vehicles.supplierBlocks.cols.by') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(b, i) in supplier_blocks"
              :key="b.id"
              :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
            >
              <td class="px-4 py-3 whitespace-nowrap text-[color:var(--inkSoft)] tabular-nums">
                {{ b.effective_start_on }} → {{ b.end_on }}
                <span
                  v-if="b.effective_start_on !== b.requested_start_on"
                  class="block text-xs text-[color:var(--muted)] mt-0.5"
                >
                  ({{ t('vehicles.supplierBlocks.requested') }} {{ b.requested_start_on }})
                </span>
              </td>
              <td class="px-4 py-3">
                <span :class="['supplier-block-pill', `supplier-block-pill--${b.status}`]">{{ b.status }}</span>
              </td>
              <td class="px-4 py-3 text-[color:var(--ink)]">
                {{ b.reason || '—' }}
              </td>
              <td class="px-4 py-3 text-[color:var(--inkSoft)]">
                {{ b.portal_account_email }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Mobile cards -->
      <div class="md:hidden divide-y divide-[color:var(--line)]">
        <div
          v-for="b in supplier_blocks"
          :key="b.id"
          class="px-4 py-3"
        >
          <div class="flex items-center justify-between gap-2 mb-1">
            <span class="text-sm fw-value tabular-nums text-[color:var(--ink)]">
              {{ b.effective_start_on }} → {{ b.end_on }}
            </span>
            <span :class="['supplier-block-pill', `supplier-block-pill--${b.status}`]">{{ b.status }}</span>
          </div>
          <div class="text-xs text-[color:var(--muted)]">
            {{ b.portal_account_email }}
          </div>
          <div v-if="b.reason" class="text-xs text-[color:var(--inkSoft)] mt-0.5">
            {{ b.reason }}
          </div>
        </div>
      </div>
      <div
        class="px-6 py-3"
        :style="{ borderTop: '1px solid var(--line)' }"
      >
        <p class="text-xs text-[color:var(--muted)]">
          {{ t('vehicles.supplierBlocks.hint') }}
        </p>
      </div>
    </div>

    <!-- Source / sub-rental — only render when the vehicle has any sub-rental
         history (current or past). Owned vehicles get no card here; their
         sub-rented status is set at creation only. -->
    <div
      v-if="current_sub_rental || sub_rental_history.length > 0"
      class="mt-6"
    >
      <UiCard>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-[color:var(--ink)]">
            {{ t('suppliers.vehicleCard.title') }}
          </h3>
          <UiButton
            v-if="released_from_supplier && isAdmin"
            variant="primary"
            size="sm"
            @click="markSubRentedOpen = true"
          >
            ↻ {{ t('suppliers.vehicleCard.renew') }}
          </UiButton>
        </div>

        <!-- Released banner -->
        <div
          v-if="released_from_supplier"
          class="rounded-[var(--radius-md)] p-3 mb-3 text-sm"
          :style="{ background: 'color-mix(in oklab, var(--warn) 10%, transparent)', border: '1px solid color-mix(in oklab, var(--warn) 30%, transparent)', color: 'var(--ink)' }"
        >
          <div class="fw-value mb-1" :style="{ color: 'var(--warn)' }">
            {{ t('suppliers.vehicleCard.releasedTitle') }}
          </div>
          <p class="text-xs text-[color:var(--inkSoft)]">
            {{ t('suppliers.vehicleCard.releasedHint') }}
          </p>
        </div>

        <div
          v-if="current_sub_rental"
          class="mb-4"
        >
          <!-- Two-column body: supplier info (left), money summary (right) -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <!-- Left: name, period, day progress, contact preview -->
            <div class="space-y-2 min-w-0">
              <div class="text-sm">
                <Link
                  :href="`/vehicle_sources/${current_sub_rental.source_id}`"
                  class="fw-value text-[color:var(--accent)] hover:underline"
                >
                  {{ current_sub_rental.source_name }}
                </Link>
                <div class="text-xs text-[color:var(--muted)] mt-0.5 tabular-nums">
                  {{ current_sub_rental.rate_display }} ·
                  {{ current_sub_rental.start_on }} – {{ current_sub_rental.end_on || t('sub_rentals.openEnded') }}
                </div>
              </div>
              <div class="text-xs text-[color:var(--inkSoft)]">
                <span v-if="current_sub_rental.days_total != null">
                  {{ t('sub_rentals.dayOf', { used: current_sub_rental.days_used, total: current_sub_rental.days_total }) }}
                  <span
                    v-if="current_sub_rental.days_left != null"
                    class="text-[color:var(--muted)]"
                  > · {{ t('sub_rentals.daysLeft', { n: current_sub_rental.days_left }) }}</span>
                </span>
                <span v-else-if="current_sub_rental.days_used > 0">
                  {{ t('sub_rentals.daysUsed', { n: current_sub_rental.days_used }) }}
                </span>
              </div>
              <ul
                v-if="current_sub_rental.source_phone || current_sub_rental.source_email || (current_sub_rental.source_channels && current_sub_rental.source_channels.length)"
                class="text-xs text-[color:var(--inkSoft)] space-y-0.5"
              >
                <li v-if="current_sub_rental.source_phone">📞 {{ current_sub_rental.source_phone }}</li>
                <li v-if="current_sub_rental.source_email">✉ {{ current_sub_rental.source_email }}</li>
                <li
                  v-for="ch in (current_sub_rental.source_channels || [])"
                  :key="ch.platform + ch.handle"
                >
                  <span class="uppercase tracking-wider text-[color:var(--muted)] mr-1">{{ ch.platform }}</span>{{ ch.handle }}
                </li>
              </ul>
            </div>

            <!-- Right: financial summary in a 2×N grid -->
            <div class="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <div v-if="current_sub_rental.total_planned">
                <div class="text-[color:var(--muted)] text-xs">
                  {{ t('sub_rentals.cols.planned') }}
                </div>
                <div class="fw-value tabular-nums">
                  {{ current_sub_rental.total_planned }}
                </div>
              </div>
              <div>
                <div class="text-[color:var(--muted)] text-xs">
                  {{ t('sub_rentals.cols.actual') }}
                </div>
                <div class="fw-value tabular-nums">
                  {{ current_sub_rental.actual_to_date }}
                </div>
              </div>
              <div>
                <div class="text-[color:var(--muted)] text-xs">
                  {{ t('sub_rentals.cols.paid') }}
                </div>
                <div class="fw-value tabular-nums">
                  {{ current_sub_rental.paid }}
                </div>
              </div>
              <div>
                <div class="text-[color:var(--muted)] text-xs">
                  {{ t('sub_rentals.cols.balance') }}
                </div>
                <div
                  class="fw-value tabular-nums"
                  :class="current_sub_rental.balance_cents > 0 ? 'text-[color:var(--warn)]' : ''"
                >
                  {{ current_sub_rental.balance }}
                </div>
              </div>
              <div v-if="current_sub_rental.deposit" class="col-span-2 pt-1 border-t border-[color:var(--line)]">
                <div class="text-[color:var(--muted)] text-xs">
                  {{ t('sub_rentals.cols.deposit') }}
                </div>
                <div class="fw-value tabular-nums">
                  {{ current_sub_rental.deposit }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isAdmin"
            class="flex flex-wrap gap-2 pt-4 mt-2 border-t border-[color:var(--line)]"
          >
            <UiButton
              variant="secondary"
              size="sm"
              @click="paymentModalOpen = true"
            >
              {{ t('supplier_payments.record') }}
            </UiButton>
            <Link
              v-if="supplierContactHref"
              :href="supplierContactHref"
            >
              <UiButton
                type="button"
                variant="secondary"
                size="sm"
              >
                {{ supplier_conversation_id ? t('suppliers.vehicleCard.openChat') : t('suppliers.vehicleCard.contact') }}
              </UiButton>
            </Link>
            <UiButton
              variant="danger"
              size="sm"
              @click="endSubRental"
            >
              {{ t('suppliers.vehicleCard.end') }}
            </UiButton>
          </div>
        </div>

        <details
          v-if="sub_rental_history.length > (current_sub_rental ? 1 : 0)"
          class="text-sm"
        >
          <summary class="cursor-pointer text-[color:var(--inkSoft)]">
            {{ t('suppliers.vehicleCard.history') }} ({{ sub_rental_history.length }})
          </summary>
          <ul class="mt-2 space-y-1 pl-4">
            <li
              v-for="h in sub_rental_history"
              :key="h.id"
              class="text-[color:var(--inkSoft)]"
            >
              <Link
                :href="`/vehicle_sources/${h.source_id}`"
                class="text-[color:var(--accent)] hover:underline"
              >
                {{ h.source_name }}
              </Link>
              · {{ h.start_on }} – {{ h.end_on || t('sub_rentals.openEnded') }} · {{ h.rate_display }}
            </li>
          </ul>
        </details>
      </UiCard>
    </div>

    <MarkSubRentedModal
      v-model:open="markSubRentedOpen"
      :vehicle="vehicle"
      :sources="available_sources"
      :prefill="renew_prefill"
    />
    <RecordSupplierPaymentModal
      v-model:open="paymentModalOpen"
      :sub-rental="current_sub_rental"
    />

    <ReportDamageModal
      :open="reportOpen"
      :vehicle-id="vehicle.id"
      :damage="editingDamage"
      @close="reportOpen = false"
    />
    <MarkRepairedModal
      :open="repairOpen"
      :damage="editingDamage"
      @close="repairOpen = false"
    />

    <ConfirmDialog
      :open="showDeleteConfirm"
      :title="t('vehicles.show.delete.title')"
      :consequence="t('vehicles.show.delete.consequence', { make: vehicle.make, model: vehicle.model, reg: vehicle.registration })"
      :confirm-text="t('vehicles.show.delete.confirm')"
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />

    <!-- Open maintenance modal -->
    <UiModal
      :open="showOpenModal"
      :title="t('vehicles.maintenance.openTitle')"
      :description="t('vehicles.maintenance.openDescription')"
      @close="showOpenModal = false"
    >
      <form
        class="space-y-4"
        @submit.prevent="openMaintenance"
      >
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
            {{ t('vehicles.maintenance.fields.description') }}
          </label>
          <UiTextarea
            v-model="openForm.description"
            :placeholder="t('vehicles.maintenance.descriptionPlaceholder')"
            rows="3"
            :error="openForm.errors.description"
          />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
              {{ t('vehicles.maintenance.fields.estimatedCost') }}
            </label>
            <UiInput
              v-model="openForm.cost_cents"
              type="number"
              placeholder="0"
              :error="openForm.errors.cost_cents"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
              {{ t('vehicles.maintenance.fields.plannedEnd') }}
            </label>
            <UiInput
              v-model="openForm.planned_end_date"
              type="date"
              :error="openForm.errors.planned_end_date"
            />
          </div>
        </div>
      </form>
      <template #footer>
        <UiButton
          variant="secondary"
          @click="showOpenModal = false"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          :loading="openForm.processing"
          :disabled="!openForm.description"
          @click="openMaintenance"
        >
          {{ t('vehicles.maintenance.openSubmit') }}
        </UiButton>
      </template>
    </UiModal>

    <!-- Close maintenance modal -->
    <UiModal
      :open="showCloseModal"
      :title="t('vehicles.maintenance.closeTitle')"
      :description="t('vehicles.maintenance.closeDescription')"
      @close="showCloseModal = false"
    >
      <form
        class="space-y-4"
        @submit.prevent="closeMaintenance"
      >
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
            {{ t('vehicles.maintenance.fields.finalCost') }}
          </label>
          <UiInput
            v-model="closeForm.cost_cents"
            type="number"
            :error="closeForm.errors.cost_cents"
          />
          <p class="text-xs text-[color:var(--muted)] mt-1">
            {{ t('vehicles.maintenance.finalCostHint') }}
          </p>
        </div>
        <div>
          <label class="block text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
            {{ t('vehicles.maintenance.fields.closeNotes') }}
          </label>
          <UiTextarea
            v-model="closeForm.notes"
            :placeholder="t('vehicles.maintenance.closeNotesPlaceholder')"
            rows="3"
            :error="closeForm.errors.notes"
          />
        </div>
      </form>
      <template #footer>
        <UiButton
          variant="secondary"
          @click="showCloseModal = false"
        >
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton
          variant="primary"
          :loading="closeForm.processing"
          @click="closeMaintenance"
        >
          {{ t('vehicles.maintenance.closeSubmit') }}
        </UiButton>
      </template>
    </UiModal>
  </PageShell>
</template>

<style scoped>
.supplier-block-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: capitalize;
}
.supplier-block-pill--queued  { background: color-mix(in oklch, var(--warn) 14%, var(--surface)); color: var(--warn); }
.supplier-block-pill--active  { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.supplier-block-pill--cleared { background: color-mix(in oklch, var(--muted) 14%, var(--surface)); color: var(--muted); }
.supplier-block-pill--expired { background: color-mix(in oklch, var(--muted) 10%, var(--surface)); color: var(--muted); }
</style>
